// Who is looking at a public page (#123 register S).
//
// The dual-audience public pages — the rules FAQ, document verification and
// the /documents deep link — are read by anonymous visitors AND by signed-in
// people who followed a link from inside the portal. Four things branch on
// which of the two is looking: the layout's chrome, the GraphQL transport,
// field-level gating, and the "back to the portal" style affordances. S3: they
// all read ONE predicate, so they can never disagree. On a hard load of a
// public page the auth boot's silent check-sso decides it (S2(d),
// src/boot/keycloak.ts); where that check cannot see a session (third-party
// cookies blocked, Keycloak slow or down) the page is the anonymous one with
// less chrome, by ruling (S8).
import { provideClient } from '@urql/vue'
import { useAuthStore } from 'src/stores/auth'
import { createPublicGraphqlClient } from 'src/core/publicGraphqlClient'
import { isPublicPath } from 'src/core/publicPaths'

/** Whether a signed-in person is looking (the mock principal counts). */
export function viewerHasSession(auth: { authenticated: boolean } = useAuthStore()): boolean {
  return auth.authenticated
}

/**
 * Hand an anonymous viewer's page the core-minted public-token client; a
 * signed-in viewer keeps the session's own client (and its act-as header).
 * Call from the page's root component: provide/inject reaches descendants,
 * never the providing component itself. Mock mode and tests keep their
 * injected client — a dedicated client would bypass the mock transport.
 */
export function provideViewerClient(): void {
  if (viewerHasSession()) return
  if (import.meta.env.USE_MOCK === 'true' || !import.meta.env.GRAPHQL_URL) return
  provideClient(createPublicGraphqlClient())
}

/**
 * Whether an in-app navigation to `path` must go through the auth boot as a
 * full page load. A pre-auth page has no token, so a tool page reached by a
 * client-side link would render with nothing behind it; the boot is what
 * signs the viewer in (or asks which realm to use). Public paths stay
 * client-side.
 */
export function needsSessionBoot(auth: { preAuth: boolean }, path: string): boolean {
  return auth.preAuth && !isPublicPath(path)
}
